import _ from 'lodash'
import type {ParsedStyleItem, Property} from '../types'
import {markPropertyToRemove, isPropertyToRemove, removePropertyToRemovePrefix} from './utils'

interface BorderRadius {
    topLeft: string
    topRight: string
    bottomLeft: string
    bottomRight: string
}

const sides = [
    'border-top-left-radius',
    'border-top-right-radius',
    'border-bottom-left-radius',
    'border-bottom-right-radius'
] as const

const borderRadiusPropertiesToRemove = ['border-radius', ...sides.map(markPropertyToRemove)]

const borderSideCssMapping = {
    topLeft: 'top-left',
    topRight: 'top-right',
    bottomLeft: 'bottom-left',
    bottomRight: 'bottom-right'
}

const borderRadiusToProperty = (borderRadius: BorderRadius, withBorderRadiusAsOneParam: boolean): Property[] => {
    const defaultValue = '0px'
    const entries = Object.entries(borderRadius) as [keyof typeof borderRadius, string][]
    if (entries.length && withBorderRadiusAsOneParam) {
        return [
            {
                prop: 'border-radius',
                cssProp: 'border-radius',
                value: `${borderRadius.topLeft ?? defaultValue} ${borderRadius.topRight ?? defaultValue} ${
                    borderRadius.bottomRight ?? defaultValue
                } ${borderRadius.bottomLeft ?? defaultValue}`
            }
        ]
    }

    const borderRadiusProperties: Property[] = []
    entries.forEach(([side, value]) => {
        const sideCss = borderSideCssMapping[side]
        borderRadiusProperties.push({
            prop: `border-${sideCss}-radius`,
            cssProp: `border-${sideCss}-radius`,
            value
        })
    })

    return borderRadiusProperties
}

const getBorderRadiusValues = (borderRadius: Property): BorderRadius => {
    const borderRadiusValues = borderRadius.value.split(' ')
    const borderRadiusLength = borderRadiusValues.length

    if (borderRadiusLength === 1) {
        const [borderRadiusValue] = borderRadiusValues
        return {
            topLeft: borderRadiusValue,
            topRight: borderRadiusValue,
            bottomLeft: borderRadiusValue,
            bottomRight: borderRadiusValue
        }
    }

    if (borderRadiusLength === 2) {
        const [borderRadiusTopLeftAndBottomRight, borderTopRightAndBottomLeft] = borderRadiusValues
        return {
            topLeft: borderRadiusTopLeftAndBottomRight,
            topRight: borderTopRightAndBottomLeft,
            bottomRight: borderRadiusTopLeftAndBottomRight,
            bottomLeft: borderTopRightAndBottomLeft
        }
    }

    if (borderRadiusLength === 3) {
        const [borderRadiusTopLeft, borderRadiusLeftAndRight, borderRadiusBottomRight] = borderRadiusValues
        return {
            topLeft: borderRadiusTopLeft,
            topRight: borderRadiusLeftAndRight,
            bottomRight: borderRadiusBottomRight,
            bottomLeft: borderRadiusLeftAndRight
        }
    }

    const [borderRadiusTopLeft, borderRadiusTopRight, borderRadiusBottomLeft, borderRadiusBottomRight] =
        borderRadiusValues

    return {
        topLeft: borderRadiusTopLeft,
        topRight: borderRadiusTopRight,
        bottomLeft: borderRadiusBottomLeft,
        bottomRight: borderRadiusBottomRight
    }
}

const handleBorderRadiusProperty = (property: Property, borderRadiusSides: BorderRadius): void => {
    if (property.cssProp === 'border-radius') {
        const {topLeft, topRight, bottomLeft, bottomRight} = getBorderRadiusValues(property)
        borderRadiusSides.topLeft = topLeft
        borderRadiusSides.topRight = topRight
        borderRadiusSides.bottomLeft = bottomLeft
        borderRadiusSides.bottomRight = bottomRight
    }

    if (sides.includes(removePropertyToRemovePrefix(property.cssProp) as (typeof sides)[number])) {
        const propParts = property.cssProp.split('-')
        const side = _.camelCase(`${propParts[1]}-${propParts[2]}`) as keyof BorderRadius
        borderRadiusSides[side] = property.value

        if (!isPropertyToRemove(property.cssProp)) {
            property.cssProp = markPropertyToRemove(property.cssProp)
        }
    }
}

const getUpdatedProperties = (
    properties: Property[],
    borderRadiusSides: Partial<BorderRadius>,
    withBorderRadiusAsOneParam: boolean
): Property[] => {
    return [
        ...properties.filter(property => !borderRadiusPropertiesToRemove.includes(property.cssProp)),
        ...borderRadiusToProperty(borderRadiusSides as BorderRadius, withBorderRadiusAsOneParam)
    ]
}

const migrateBorderRadiusFactory = (
    withBorderRadiusAsOneParam: boolean
): ((styleItems: ParsedStyleItem[]) => ParsedStyleItem[]) => {
    return function (styleItems) {
        return styleItems.map(styleItem => {
            const {parsedStyle} = styleItem
            const updatedParsedStyle = Object.entries(parsedStyle).reduce(
                (acc, [selector, {properties, mobileProperties}]) => {
                    const borderRadiusSides = {} as BorderRadius
                    const mobileBorderRadiusSides = {} as BorderRadius

                    properties.forEach(property => {
                        handleBorderRadiusProperty(property, borderRadiusSides)
                    })

                    mobileProperties.forEach(property => {
                        handleBorderRadiusProperty(property, mobileBorderRadiusSides)
                    })

                    const updatedProperties = getUpdatedProperties(
                        properties,
                        borderRadiusSides,
                        withBorderRadiusAsOneParam
                    )
                    const updatedMobileProperties = getUpdatedProperties(
                        mobileProperties,
                        mobileBorderRadiusSides,
                        withBorderRadiusAsOneParam
                    )

                    return {
                        ...acc,
                        [selector]: {
                            ...acc[selector],
                            properties: updatedProperties,
                            mobileProperties: updatedMobileProperties
                        }
                    }
                },
                parsedStyle
            )

            return {
                ...styleItem,
                parsedStyle: updatedParsedStyle
            }
        })
    }
}

export default migrateBorderRadiusFactory
